import { request } from '@/api/v2/core';
import * as MigrationTypes from '@/@types/migrationTypes';
import * as ProcessingResultsTypes from '@/@types/processingResultsTypes';

/**
 * Get all migration poll steps for the specified result
 * @param {string} resultId ID of the result to get poll steps for
 * @returns {Promise<MigrationTypes.MigrationPollStep[]>}
 */
export const getResultMigrationSteps = (resultId) =>
  request(`migration/${resultId}`, {
    method: 'GET'
  });

/**
 * Create new migration poll step for the specified result
 * @param {string} resultId ID of the result to create poll step for
 * @param {MigrationTypes.MigrationPollStepCreation} data The poll step data
 * @returns {Promise<MigrationTypes.MigrationPollStep>}
 */
export const createMigrationStep = (resultId, data) =>
  request(`migration/${resultId}`, {
    data,
    method: 'POST'
  });

/**
 * Update values for an existing migration poll step for the specified result
 * @param {string} resultId ID of the result
 * @param {string} stepName Name of the step to update
 * @param {MigrationTypes.MigrationPollStepCreation} data New values for the poll step
 * @returns {Promise<MigrationTypes.MigrationPollStep>}
 */
export const updateMigrationStep = (resultId, stepName, data) =>
  request(`migration/${resultId}`, {
    data,
    method: 'PATCH',
    params: {
      stepName
    }
  });

/**
 * Save a signature image in GCS for the specified result
 * @param {string} resultId ID of the result
 * @param {MigrationTypes.Signature} data Signature data
 * @returns {Promise<string>} "Signed" to indicate signature was saved
 */
export const saveSignature = (resultId, data) =>
  request(`migration/${resultId}/signature`, {
    data,
    method: 'PUT'
  });

/**
 * Get statistics on multimatch rows for the specified result
 * @param {string} resultId ID of the result
 * @param {string[]} params List of multimatch params
 * @returns {Promise<MigrationTypes.MultimatchStatistics>}
 */
export const getMultimatchStatistics = (resultId, params) =>
  request(`migration/${resultId}/multimatch/statistics`, {
    params,
    method: 'GET'
  });

/**
 * Get counts of various rows for the specified result
 * @param {string} resultId ID of the result
 * @returns {Promise<MigrationTypes.MigrationStatistics>}
 */
export const getMigrationStatistics = (resultId) =>
  request(`migration/${resultId}/statistics`, {
    method: 'GET'
  });

/**
 * Get counts and revenue by mapped type for the specified result
 * @param {string} resultId ID of the result
 * @param {Boolean?} excludeIgnored Whether to exclude ignored items
 * @returns {Promise<MigrationTypes.TypesStatistics[]>}
 */
export const getMigrationTypesStatistics = (resultId, excludeIgnored) =>
  request(`migration/${resultId}/types-statistics`, {
    method: 'GET',
    params: {
      excludeIgnored
    }
  });

/**
 * Get statistics on imported and ignored rows for the specified result
 * @param {string} resultId ID of the result
 * @returns {Promise<MigrationTypes.ImportedIgnoredStatistics[]}
 */
export const getImportedIgnoredStatistics = (resultId) =>
  request(`migration/${resultId}/imported-ignored-statistics`, {
    method: 'GET'
  });

/**
 * Get counts of various rows sorted into buckets for the specified result
 * @param {string} resultId ID of the result
 * @returns {Promise<MigrationTypes.BucketsData>}
 */
export const getMigrationBucketsStatistics = (resultId) =>
  request(`migration/${resultId}/buckets-statistics`, {
    method: 'GET'
  });

/**
 * Get sales statistics for the specified result
 * @param {string} resultId ID of the result
 * @returns {Promise<MigrationTypes.SalesStatistics>}
 */
export const getMigrationSalesStatistics = (resultId) =>
  request(`migration/${resultId}/sales-statistics`, {
    method: 'GET'
  });

/**
 * Get statistics on reasons rows are ignored for the specified result
 * @param {string} resultId ID of the result
 * @returns {Promise<MigrationTypes.IgnoreReasonsStatistics[]>}
 */
export const getMigrationIgnoreReasonsStatistics = (resultId) =>
  request(`migration/${resultId}/ignore-reasons-statistics`, {
    method: 'GET'
  });

/**
 * Update the multimatch status for the specified row in the specified result
 * @param {string} resultId ID of the result
 * @param {string} resultRowId ID of the row to update
 * @param {Boolean} isResolved Whether the row is resolved or not
 * @returns {Promise<ProcessingResultsTypes.ProcessingResultRow>}
 */
export const updateMultimatchResolving = (resultId, resultRowId, isResolved) =>
  request(`migration/${resultId}/multimatch`, {
    method: 'PATCH',
    data: {
      isResolved
    },
    params: {
      resultRowId
    }
  });

/**
 * Retrieve rows for the specified result
 * @param {string} resultId ID of the result
 * @param {Object} params Parameters for filtering the results
 * @returns {Promise<Object.<string,MigrationTypes.MigrationResultRow[]>>}
 */
export const fetchMigrationResultRows = (resultId, params) =>
  request(`migration/${resultId}/results/rows`, {
    params,
    method: 'GET'
  });
